import LockerPointSettings from './LockerPointSettings';
import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Alert,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import api from "../../api/api";
import DocumentEditor from "../../business/rental/DocumentEditor";
import OfferVersionsEditor from "./OfferVersionsEditor";

// Список доступных часовых поясов
const TIMEZONES = {
  "Europe/Moscow": "Москва (UTC+3)",
  "Europe/Samara": "Самара (UTC+4)",
  "Asia/Yekaterinburg": "Екатеринбург (UTC+5)",
  "Asia/Omsk": "Омск (UTC+6)",
  "Asia/Krasnoyarsk": "Красноярск (UTC+7)",
  "Asia/Irkutsk": "Иркутск (UTC+8)",
  "Asia/Yakutsk": "Якутск (UTC+9)",
  "Asia/Vladivostok": "Владивосток (UTC+10)",
  "Asia/Magadan": "Магадан (UTC+11)",
  "Asia/Kamchatka": "Камчатка (UTC+12)",
  "Europe/Kaliningrad": "Калининград (UTC+2)",
  "Asia/Tashkent": "Ташкент (UTC+5)",
  "Asia/Almaty": "Алматы (UTC+6)",
  "Europe/Minsk": "Минск (UTC+3)",
  "Europe/Kiev": "Киев (UTC+2)"
};

// Универсальная функция для форматирования цен
const formatPrice = (price) => {
  if (!price && price !== 0) return "";
  return parseFloat(price).toFixed(2);
};

function RentalBusinessSettings() {
  const savingRef = useRef(false);
  const [loadErrors, setLoadErrors] = useState({});
  // Состояние для управления аккордеоном
  const [expandedPanel, setExpandedPanel] = useState(false);

  // Состояния для тарифов
  const [tariffs, setTariffs] = useState([]);
  const [tariffDialogOpen, setTariffDialogOpen] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const [newTariff, setNewTariff] = useState({
    name: "",
    price: "",
    days: 0,
    hours: 1,
    minutes: 0,
    start_after_days: 0,
  });

  // Состояния для пунктов выдачи
  const [rentalPoints, setRentalPoints] = useState([]);
  const [pointDialogOpen, setPointDialogOpen] = useState(false);
  const [editingPoint, setEditingPoint] = useState(null);
  const [newPoint, setNewPoint] = useState({
    name: "",
    address: "",
    phone: "",
    working_hours: "",
    description: "",
    timezone: "Europe/Moscow",
        has_cells: false, cell_count: 0,
  });

  // Расписание пункта (JSONB)
  const _defaultSchedule = () => Object.fromEntries(
    Array.from({ length: 7 }, (_, i) => [String(i), { working: i < 5, open: "09:00", close: "18:00" }])
  );
  const [pointSchedule, setPointSchedule] = useState(_defaultSchedule());
  // Закрытые дни (closures)
  const [pointClosures, setPointClosures] = useState([]); // [{date, reason}]
  const [newClosureDate, setNewClosureDate] = useState("");
  const [newClosureReason, setNewClosureReason] = useState("");

  // Состояния для статусов
  const [statuses, setStatuses] = useState([]);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [newStatus, setNewStatus] = useState({
    name: "",
  });

  // Состояния для источников привлечения
  const [attractionSources, setAttractionSources] = useState([]);
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [newSource, setNewSource] = useState({
    name: "",
    description: "",
  });

  // Состояния для категорий расходов
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expenseCategoryDialogOpen, setExpenseCategoryDialogOpen] = useState(false);
  const [editingExpenseCategory, setEditingExpenseCategory] = useState(null);
  const [newExpenseCategory, setNewExpenseCategory] = useState({
    name: "",
  });

  // Состояния для договоров
  const [contracts, setContracts] = useState([]);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [newContract, setNewContract] = useState({
    name: "",
    description: "",
  });

  // Состояния для редактора документов
  const [documentEditorOpen, setDocumentEditorOpen] = useState(false);
  const [editingDocumentId, setEditingDocumentId] = useState(null);

  // Обработчик изменения аккордеона
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  // Загрузка данных при монтировании
  useEffect(() => {
    fetchTariffs();
    fetchRentalPoints();
    fetchStatuses();
    fetchAttractionSources();
    fetchExpenseCategories();
    fetchContracts();
  }, []);

  // ============ ИСТОЧНИКИ ПРИВЛЕЧЕНИЯ ============
  const fetchAttractionSources = async () => {
    try {
      setLoadErrors(previous => ({ ...previous, AttractionSources: null }));
      const response = await api.get("/attraction-sources/", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setAttractionSources(response.data);
    } catch (error) {
      setLoadErrors(previous => ({ ...previous, AttractionSources: "Не удалось загрузить источники привлечения" }));
      console.error("Ошибка загрузки источников привлечения:", error);
    }
  };

  const handleSaveSource = async () => {
    if (savingRef.current) return;
    if (!newSource.name.trim()) {
      alert("Введите название источника привлечения");
      return;
    }
    savingRef.current = true;
    try {
      const sourceData = {
        name: newSource.name,
        description: newSource.description,
      };

      if (editingSource) {
        await api.put(`/attraction-sources/${editingSource.id}`, sourceData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      } else {
        await api.post("/attraction-sources/", sourceData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      }

      setSourceDialogOpen(false);
      setEditingSource(null);
      setNewSource({
        name: "",
        description: "",
      });
      fetchAttractionSources();
    } catch (error) {
      console.error("Ошибка сохранения источника привлечения:", error);
      alert("Ошибка при сохранении источника привлечения");
    } finally {
      savingRef.current = false;
    }
  };

  const handleEditSource = (source) => {
    setEditingSource(source);
    setNewSource({
      name: source.name,
      description: source.description || "",
    });
    setSourceDialogOpen(true);
  };

  const handleDeleteSource = async (sourceId) => {
    if (
      window.confirm(
        "Вы уверены, что хотите удалить этот источник привлечения?"
      )
    ) {
      try {
        await api.delete(`/attraction-sources/${sourceId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        fetchAttractionSources();
      } catch (error) {
        console.error("Ошибка удаления источника привлечения:", error);
        alert("Ошибка при удалении источника привлечения");
      }
    }
  };

  // ============ КАТЕГОРИИ РАСХОДОВ ============
  const fetchExpenseCategories = async () => {
    try {
      setLoadErrors(previous => ({ ...previous, ExpenseCategories: null }));
      const response = await api.get("/expenses/categories", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setExpenseCategories(response.data);
    } catch (error) {
      setLoadErrors(previous => ({ ...previous, ExpenseCategories: "Не удалось загрузить категории расходов" }));
      console.error("Ошибка загрузки категорий расходов:", error);
    }
  };

  const transliterate = (text) => {
    const map = {
      'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo',
      'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
      'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
      'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
      'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya',
      ' ': '_'
    };
    
    return text
      .toLowerCase()
      .split('')
      .map(char => map[char] || char)
      .join('')
      .replace(/[^a-z0-9_]/g, '');
  };

  const handleSaveExpenseCategory = async () => {
    if (savingRef.current) return;
    if (!newExpenseCategory.name.trim()) {
      alert("Введите название категории");
      return;
    }
    savingRef.current = true;
    try {
      if (!newExpenseCategory.name.trim()) {
        alert('Введите название категории');
        return;
      }

      const code = transliterate(newExpenseCategory.name.trim());
      const categoryData = {
        name: newExpenseCategory.name.trim(),
        code: code
      };

      if (editingExpenseCategory) {
        await api.put(`/expenses/categories/${editingExpenseCategory.id}`, categoryData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      } else {
        await api.post("/expenses/categories", categoryData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      }

      setExpenseCategoryDialogOpen(false);
      setEditingExpenseCategory(null);
      setNewExpenseCategory({ name: "" });
      fetchExpenseCategories();
    } catch (error) {
      console.error("Ошибка сохранения категории расходов:", error);
      alert("Ошибка при сохранении категории расходов: " + (error.response?.data?.detail || error.message));
    } finally {
      savingRef.current = false;
    }
  };

  const handleEditExpenseCategory = (category) => {
    setEditingExpenseCategory(category);
    setNewExpenseCategory({
      name: category.name,
    });
    setExpenseCategoryDialogOpen(true);
  };

  const handleDeleteExpenseCategory = async (categoryId) => {
    if (window.confirm("Вы уверены, что хотите удалить эту категорию расходов?")) {
      try {
        await api.delete(`/expenses/categories/${categoryId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        fetchExpenseCategories();
      } catch (error) {
        console.error("Ошибка удаления категории расходов:", error);
        alert("Ошибка при удалении категории расходов");
      }
    }
  };

  // ============ ДОГОВОРЫ ============
  const fetchContracts = async () => {
    try {
      setLoadErrors(previous => ({ ...previous, Contracts: null }));
      const response = await api.get("/documents/templates/", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setContracts(response.data);
    } catch (error) {
      setLoadErrors(previous => ({ ...previous, Contracts: "Не удалось загрузить договоры" }));
      console.error("Ошибка загрузки договоров:", error);
      setContracts([]);
    }
  };

  const handleSaveContract = async () => {
    if (savingRef.current) return;
    if (!newContract.name.trim()) {
      alert("Введите название договора");
      return;
    }
    savingRef.current = true;
    try {
      const contractData = {
        name: newContract.name,
        description: newContract.description,
        category: "contract",
        is_system: false,
        is_active: true,
      };

      if (editingContract) {
        await api.put(`/documents/templates/${editingContract.id}`, contractData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      } else {
        await api.post("/documents/templates/", contractData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      }

      setContractDialogOpen(false);
      setEditingContract(null);
      setNewContract({
        name: "",
        description: "",
      });
      fetchContracts();
    } catch (error) {
      console.error("Ошибка сохранения договора:", error);
      alert("Ошибка при сохранении договора");
    } finally {
      savingRef.current = false;
    }
  };

  const handleEditContract = (contract) => {
    setEditingContract(contract);
    setNewContract({
      name: contract.name,
      description: contract.description || "",
    });
    setContractDialogOpen(true);
  };

  const handleDeleteContract = async (contractId) => {
    if (window.confirm("Вы уверены, что хотите удалить этот договор?")) {
      try {
        await api.delete(`/documents/templates/${contractId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        fetchContracts();
      } catch (error) {
        console.error("Ошибка удаления договора:", error);
        alert("Ошибка при удалении договора");
      }
    }
  };

  const openDocumentEditor = (contractId) => {
    setEditingDocumentId(contractId);
    setDocumentEditorOpen(true);
  };

  // ============ ТАРИФЫ ============
  const fetchTariffs = async () => {
    try {
      setLoadErrors(previous => ({ ...previous, Tariffs: null }));
      const response = await api.get("/rental-tariffs/", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setTariffs(response.data);
    } catch (error) {
      setLoadErrors(previous => ({ ...previous, Tariffs: "Не удалось загрузить тарифы" }));
      console.error("Ошибка загрузки тарифов:", error);
    }
  };

  const handleSaveTariff = async () => {
    if (savingRef.current) return;
    if (String(newTariff.price).trim() === '' || !Number.isFinite(Number(newTariff.price)) || Number(newTariff.price) < 0 ||
        ['days', 'hours', 'minutes', 'start_after_days'].some(key => !Number.isInteger(Number(newTariff[key] || 0)) || Number(newTariff[key]) < 0)) {
      alert('Укажите неотрицательную цену и целую длительность тарифа');
      return;
    }
    if (!newTariff.name.trim()) {
      alert("Введите название тарифа");
      return;
    }
    savingRef.current = true;
    try {
      const tariffData = {
        name: newTariff.name,
        price: parseFloat(newTariff.price),
        days: parseInt(newTariff.days) || 0,
        hours: parseInt(newTariff.hours) || 0,
        minutes: parseInt(newTariff.minutes) || 0,
        start_after_days: parseInt(newTariff.start_after_days) || 0,
        asset_id: null, // Глобальные тарифы без привязки к активу
      };

      if (editingTariff) {
        await api.put(`/rental-tariffs/${editingTariff.id}`, tariffData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      } else {
        await api.post("/rental-tariffs/", tariffData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      }

      setTariffDialogOpen(false);
      setEditingTariff(null);
      setNewTariff({
        name: "",
        price: "",
        days: 0,
        hours: 1,
        minutes: 0,
        start_after_days: 0,
      });
      fetchTariffs();
    } catch (error) {
      console.error("Ошибка сохранения тарифа:", error);
      alert("Ошибка при сохранении тарифа");
    } finally {
      savingRef.current = false;
    }
  };

  const handleEditTariff = (tariff) => {
    setEditingTariff(tariff);
    setNewTariff({
      name: tariff.name,
      price: tariff.price.toString(),
      days: tariff.days,
      hours: tariff.hours,
      minutes: tariff.minutes,
      start_after_days: tariff.start_after_days,
    });
    setTariffDialogOpen(true);
  };

  const handleDeleteTariff = async (tariffId) => {
    if (window.confirm("Вы уверены, что хотите удалить этот тариф?")) {
      try {
        await api.delete(`/rental-tariffs/${tariffId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        fetchTariffs();
      } catch (error) {
        console.error("Ошибка удаления тарифа:", error);
        
        // Проверяем, если это ошибка 400 (тариф используется), показываем как предупреждение
        if (error.response && error.response.status === 400 && error.response.data && error.response.data.detail) {
          // Импортируем ErrorService если еще не импортирован
          import('../../../services/ErrorService').then(({ default: ErrorService }) => {
            ErrorService.showWarning(error.response.data.detail);
          });
        } else {
          // Для других ошибок показываем как обычную ошибку
          import('../../../services/ErrorService').then(({ default: ErrorService }) => {
            ErrorService.showError(error, "Ошибка при удалении тарифа");
          });
        }
      }
    }
  };

  // ============ ПУНКТЫ ВЫДАЧИ ============
  const fetchRentalPoints = async () => {
    try {
      setLoadErrors(previous => ({ ...previous, RentalPoints: null }));
      const response = await api.get("/rental-points/", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRentalPoints(response.data);
    } catch (error) {
      setLoadErrors(previous => ({ ...previous, RentalPoints: "Не удалось загрузить пункты выдачи" }));
      console.error("Ошибка загрузки пунктов выдачи:", error);
    }
  };

  const handleSavePoint = async () => {
    if (savingRef.current) return;
    if (!newPoint.name.trim()) {
      alert("Введите название пункта выдачи");
      return;
    }
    savingRef.current = true;
    try {
      const payload = { ...newPoint, schedule: pointSchedule, closures: pointClosures };
      if (editingPoint) {
        await api.put(`/rental-points/${editingPoint.id}`, payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      } else {
        await api.post("/rental-points/", payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      }

      setPointDialogOpen(false);
      setEditingPoint(null);
      setNewPoint({
        name: "",
        address: "",
        phone: "",
        working_hours: "",
        description: "",
        timezone: "Europe/Moscow",
        has_cells: false, cell_count: 0,
      });
      setPointSchedule(_defaultSchedule());
      setPointClosures([]);
      setNewClosureDate("");
      setNewClosureReason("");
      fetchRentalPoints();
    } catch (error) {
      console.error("Ошибка сохранения пункта выдачи:", error);
      alert(error.response?.data?.detail || "Ошибка при сохранении пункта выдачи");
    } finally {
      savingRef.current = false;
    }
  };

  const handleEditPoint = (point) => {
    setEditingPoint(point);
    setNewPoint({
      name: point.name || "",
      address: point.address || "",
      phone: point.phone || "",
      working_hours: point.working_hours || "",
      description: point.description || "",
      timezone: point.timezone || "Europe/Moscow",
      has_cells: !!point.has_cells, cell_count: point.cell_count || 0,
    });
    setPointSchedule(point.schedule || _defaultSchedule());
    setPointClosures(point.closures || []);
    setNewClosureDate("");
    setNewClosureReason("");
    setPointDialogOpen(true);
  };

  const handleDeletePoint = async (pointId) => {
    if (window.confirm("Вы уверены, что хотите удалить этот пункт выдачи?")) {
      try {
        await api.delete(`/rental-points/${pointId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        fetchRentalPoints();
      } catch (error) {
        console.error("Ошибка удаления пункта выдачи:", error);
        alert("Ошибка при удалении пункта выдачи");
      }
    }
  };

  // ============ СТАТУСЫ ============
  const fetchStatuses = async () => {
    try {
      setLoadErrors(previous => ({ ...previous, Statuses: null }));
      const response = await api.get("/asset-statuses/", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setStatuses(response.data);
    } catch (error) {
      setLoadErrors(previous => ({ ...previous, Statuses: "Не удалось загрузить статусы" }));
      console.error("Ошибка загрузки статусов:", error);
    }
  };
  const handleSaveStatus = async () => {
    if (savingRef.current) return;
    if (!newStatus.name.trim()) {
      alert("Введите название статуса");
      return;
    }
    savingRef.current = true;
    try {
      if (editingStatus) {
        // Дополнительная проверка для системных статусов
        if (editingStatus.is_system) {
          alert("Системные статусы нельзя изменять");
          return;
        }

        await api.put(
          `/asset-statuses/${editingStatus.id}`,
          { name: newStatus.name },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      } else {
        await api.post(
          "/asset-statuses/",
          { name: newStatus.name },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      }

      setStatusDialogOpen(false);
      setEditingStatus(null);
      setNewStatus({ name: "" });
      fetchStatuses();
    } catch (error) {
      console.error("Ошибка сохранения статуса:", error);

      // Более информативные сообщения об ошибках
      if (
        error.response?.status === 400 &&
        error.response?.data?.detail?.includes("системные")
      ) {
        alert("Системные статусы нельзя изменять");
      } else if (
        error.response?.status === 400 &&
        error.response?.data?.detail?.includes("уже существует")
      ) {
        alert("Статус с таким именем уже существует");
      } else {
        alert("Ошибка при сохранении статуса");
      }
    } finally {
      savingRef.current = false;
    }
  };
  const handleEditStatus = (status) => {
    if (status.is_system) {
      alert("Системные статусы нельзя изменять");
      return;
    }

    setEditingStatus(status);
    setNewStatus({ name: status.name });
    setStatusDialogOpen(true);
  };
  const handleDeleteStatus = async (statusId) => {
    // Найдем статус для проверки
    const status = statuses.find((s) => s.id === statusId);

    if (status?.is_system) {
      alert("Системные статусы нельзя удалять");
      return;
    }

    if (window.confirm("Вы уверены, что хотите удалить этот статус?")) {
      try {
        await api.delete(`/asset-statuses/${statusId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        fetchStatuses();
      } catch (error) {
        console.error("Ошибка удаления статуса:", error);

        // Более информативные сообщения об ошибках
        if (
          error.response?.status === 400 &&
          error.response?.data?.detail?.includes("системный")
        ) {
          alert("Нельзя удалить системный статус");
        } else if (
          error.response?.status === 400 &&
          error.response?.data?.detail?.includes("используется")
        ) {
          alert("Нельзя удалить статус, который используется в активах");
        } else {
          alert("Ошибка при удалении статуса");
        }
      }
    }
  };

  const formatTariffTime = (tariff) => {
    const parts = [];
    if (tariff.days > 0) parts.push(`${tariff.days} дн.`);
    if (tariff.hours > 0) parts.push(`${tariff.hours} ч.`);
    if (tariff.minutes > 0) parts.push(`${tariff.minutes} мин.`);
    return parts.join(" ") || "0 мин.";
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Настройки проката
      </Typography>

      {Object.entries(loadErrors).filter(([, message]) => message).map(([key, message]) => <Alert key={key} severity="error" sx={{ mb: 1 }}>{message}</Alert>)}

      {/* ============ АККОРДЕОН ТАРИФЫ ============ */}
      <Accordion
        expanded={expandedPanel === "tariffs"}
        onChange={handleAccordionChange("tariffs")}
        sx={{ mt: 2 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="tariffs-content"
          id="tariffs-header"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography variant="h6">Тарифы ({tariffs.length})</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.stopPropagation(); // Предотвращаем закрытие аккордеона
                setEditingTariff(null);
                setNewTariff({
                  name: "",
                  price: "",
                  days: 0,
                  hours: 1,
                  minutes: 0,
                  start_after_days: 0,
                });
                setTariffDialogOpen(true);
              }}
              sx={{ mr: 2 }}
            >
              Добавить
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Название</TableCell>
                  <TableCell>Время</TableCell>
                  <TableCell>Цена</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tariffs.map((tariff) => (
                  <TableRow key={tariff.id}>
                    <TableCell>{tariff.name}</TableCell>
                    <TableCell>{formatTariffTime(tariff)}</TableCell>
                    <TableCell>{formatPrice(tariff.price)} ₽</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditTariff(tariff)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteTariff(tariff.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* ============ АККОРДЕОН ПУНКТЫ ВЫДАЧИ ============ */}
      <Accordion
        expanded={expandedPanel === "points"}
        onChange={handleAccordionChange("points")}
        sx={{ mt: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="points-content"
          id="points-header"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography variant="h6">
              Пункты выдачи ({rentalPoints.length})
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.stopPropagation();
                setEditingPoint(null);
                setNewPoint({
                  name: "",
                  address: "",
                  phone: "",
                  working_hours: "",
                  description: "",
                  timezone: "Europe/Moscow",
        has_cells: false, cell_count: 0,
                });
                setPointSchedule(_defaultSchedule());
                setPointClosures([]);
                setNewClosureDate("");
                setNewClosureReason("");
                setPointDialogOpen(true);
              }}
              sx={{ mr: 2 }}
            >
              Добавить
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Название</TableCell>
                  <TableCell>Адрес</TableCell>
                  <TableCell>Телефон</TableCell>
                  <TableCell>Время работы</TableCell>
                  <TableCell>Часовой пояс</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rentalPoints.map((point) => (
                  <TableRow key={point.id}>
                    <TableCell>{point.name}</TableCell>
                    <TableCell>{point.address || "—"}</TableCell>
                    <TableCell>{point.phone || "—"}</TableCell>
                    <TableCell>{point.working_hours || "—"}</TableCell>
                    <TableCell>
                      <Chip 
                        label={TIMEZONES[point.timezone] || point.timezone || "Europe/Moscow"}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditPoint(point)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeletePoint(point.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* ============ АККОРДЕОН СТАТУСЫ ============ */}
      <Accordion
        expanded={expandedPanel === "statuses"}
        onChange={handleAccordionChange("statuses")}
        sx={{ mt: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="statuses-content"
          id="statuses-header"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography variant="h6">
              Статусы активов ({statuses.length})
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.stopPropagation();
                setEditingStatus(null);
                setNewStatus({ name: "" });
                setStatusDialogOpen(true);
              }}
              sx={{ mr: 2 }}
            >
              Добавить
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table>
              {" "}
              <TableHead>
                <TableRow>
                  <TableCell>Название статуса</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>{" "}
              <TableBody>
                {statuses.map((status) => (
                  <TableRow key={status.id}>
                    <TableCell>
                      {status.name}
                      {status.is_system && (
                        <Box
                          component="span"
                          sx={{ ml: 1, color: "text.secondary" }}
                        >
                          🔒
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {status.is_system ? (
                        <Chip
                          label="Системный"
                          size="small"
                          color="default"
                          icon={<SecurityIcon />}
                        />
                      ) : (
                        <Chip
                          label="Пользовательский"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {!status.is_system ? (
                        <>
                          <IconButton onClick={() => handleEditStatus(status)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteStatus(status.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      ) : (
                        <Box
                          sx={{ color: "text.secondary", fontStyle: "italic" }}
                        >
                          Защищено
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* ============ АККОРДЕОН ИСТОЧНИКИ ПРИВЛЕЧЕНИЯ ============ */}
      <Accordion
        expanded={expandedPanel === "sources"}
        onChange={handleAccordionChange("sources")}
        sx={{ mt: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="sources-content"
          id="sources-header"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography variant="h6">
              Источники привлечения ({attractionSources.length})
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.stopPropagation();
                setEditingSource(null);
                setNewSource({ name: "", description: "" });
                setSourceDialogOpen(true);
              }}
              sx={{ mr: 2 }}
            >
              Добавить
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Название</TableCell>
                  <TableCell>Описание</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attractionSources.map((source) => (
                  <TableRow key={source.id}>
                    <TableCell>{source.name}</TableCell>
                    <TableCell>{source.description || "—"}</TableCell>
                    <TableCell>
                      <Chip 
                        label={source.is_system ? "Системный" : "Пользовательский"} 
                        color={source.is_system ? "default" : "primary"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {!source.is_system ? (
                        <>
                          <IconButton onClick={() => handleEditSource(source)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteSource(source.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </>
                      ) : (
                        <Chip label="Только для чтения" size="small" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* ============ АККОРДЕОН КАТЕГОРИИ РАСХОДОВ ============ */}
      <Accordion
        expanded={expandedPanel === "expenseCategories"}
        onChange={handleAccordionChange("expenseCategories")}
        sx={{ mt: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="expenseCategories-content"
          id="expenseCategories-header"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography variant="h6">
              Категории расходов ({expenseCategories.length})
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.stopPropagation();
                setEditingExpenseCategory(null);
                setNewExpenseCategory({ name: "" });
                setExpenseCategoryDialogOpen(true);
              }}
              sx={{ mr: 2 }}
            >
              Добавить
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Название</TableCell>
                  <TableCell>Код</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenseCategories.length > 0 ? (
                  expenseCategories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell>{category.name}</TableCell>
                      <TableCell><code>{category.code}</code></TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleEditExpenseCategory(category)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteExpenseCategory(category.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <em>Категорий расходов пока нет</em>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* ============ АККОРДЕОН ДОГОВОРЫ ============ */}
      <Accordion
        expanded={expandedPanel === "contracts"}
        onChange={handleAccordionChange("contracts")}
        sx={{ mt: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="contracts-content"
          id="contracts-header"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography variant="h6">
              Договоры ({contracts.length})
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.stopPropagation();
                setEditingContract(null);
                setNewContract({
                  name: "",
                  template_content: "",
                  description: "",
                  is_default: false,
                });
                setContractDialogOpen(true);
              }}
              sx={{ mr: 2 }}
            >
              Добавить
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Название</TableCell>
                  <TableCell>Описание</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{contracts.map((contract) => (
                  <TableRow key={contract.id}>
                    <TableCell>{contract.name}</TableCell>
                    <TableCell>{contract.description || "—"}</TableCell>
                    <TableCell>
                      <Chip 
                        label={contract.is_default ? "По умолчанию" : "Дополнительный"} 
                        color={contract.is_default ? "primary" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditContract(contract)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => openDocumentEditor(contract.id)}
                        title="Редактировать содержимое"
                      >
                        <DescriptionIcon />
                      </IconButton>
                      {!contract.is_default && (
                        <IconButton onClick={() => handleDeleteContract(contract.id)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}</TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* ============ АККОРДЕОН ОФЕРТЫ (онлайн-портал) ============ */}
      <OfferVersionsEditor
        expanded={expandedPanel === "offer"}
        onChange={handleAccordionChange("offer")}
      />

      {/* ============ ДИАЛОГ ТАРИФА ============ */}
      <Dialog
        open={tariffDialogOpen}
        onClose={() => setTariffDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingTariff ? "Редактировать тариф" : "Добавить тариф"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Название тарифа"
                fullWidth
                value={newTariff.name}
                onChange={(e) =>
                  setNewTariff({ ...newTariff, name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Цена (₽)"
                type="number"
                step="0.01"
                fullWidth
                value={newTariff.price}
                onChange={(e) =>
                  setNewTariff({ ...newTariff, price: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Дни"
                type="number"
                fullWidth
                value={newTariff.days}
                onChange={(e) =>
                  setNewTariff({
                    ...newTariff,
                    days: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Часы"
                type="number"
                fullWidth
                value={newTariff.hours}
                onChange={(e) =>
                  setNewTariff({
                    ...newTariff,
                    hours: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Минуты"
                type="number"
                fullWidth
                value={newTariff.minutes}
                onChange={(e) =>
                  setNewTariff({
                    ...newTariff,
                    minutes: e.target.value,
                  })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTariffDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleSaveTariff} variant="contained">
            {editingTariff ? "Сохранить" : "Добавить"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ ДИАЛОГ ПУНКТА ВЫДАЧИ ============ */}
      <Dialog
        open={pointDialogOpen}
        onClose={() => setPointDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingPoint
            ? "Редактировать пункт выдачи"
            : "Добавить пункт выдачи"}
        </DialogTitle>
        <DialogContent>
          <LockerPointSettings pointId={editingPoint?.id} value={newPoint} onChange={setNewPoint} />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Название пункта"
                fullWidth
                value={newPoint.name}
                onChange={(e) =>
                  setNewPoint({ ...newPoint, name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Адрес"
                fullWidth
                value={newPoint.address}
                onChange={(e) =>
                  setNewPoint({ ...newPoint, address: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Телефон"
                fullWidth
                value={newPoint.phone}
                onChange={(e) =>
                  setNewPoint({ ...newPoint, phone: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Время работы"
                fullWidth
                value={newPoint.working_hours}
                onChange={(e) =>
                  setNewPoint({ ...newPoint, working_hours: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Описание"
                fullWidth
                multiline
                rows={3}
                value={newPoint.description}
                onChange={(e) =>
                  setNewPoint({ ...newPoint, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="timezone-select-label">Часовой пояс</InputLabel>
                <Select
                  labelId="timezone-select-label"
                  id="timezone-select"
                  value={newPoint.timezone}
                  label="Часовой пояс"
                  onChange={(e) => setNewPoint({ ...newPoint, timezone: e.target.value })}
                >
                  {Object.entries(TIMEZONES).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* ── Расписание + Закрытые дни рядом ── */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexWrap: "wrap" }}>

                {/* Расписание пн-вс */}
                <Box sx={{ flex: "0 0 auto" }}>
                  <Typography variant="subtitle2" gutterBottom>Расписание (по дням недели)</Typography>
                  {["Пн","Вт","Ср","Чт","Пт","Сб","Вс"].map((dayLabel, idx) => {
                    const key = String(idx);
                    const day = pointSchedule[key] || { working: false, open: "09:00", close: "18:00" };
                    return (
                      <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!day.working}
                              size="small"
                              onChange={(e) => setPointSchedule(s => ({ ...s, [key]: { ...day, working: e.target.checked } }))}
                            />
                          }
                          label={<Typography sx={{ width: 28, fontSize: 13 }}>{dayLabel}</Typography>}
                          sx={{ minWidth: 90 }}
                        />
                        <TextField
                          type="time"
                          size="small"
                          value={day.open || "09:00"}
                          disabled={!day.working}
                          onChange={(e) => setPointSchedule(s => ({ ...s, [key]: { ...day, open: e.target.value } }))}
                          inputProps={{ step: 300 }}
                          sx={{ width: 110 }}
                          label="Открытие"
                        />
                        <Typography sx={{ fontSize: 12 }}>—</Typography>
                        <TextField
                          type="time"
                          size="small"
                          value={day.close || "18:00"}
                          disabled={!day.working}
                          onChange={(e) => setPointSchedule(s => ({ ...s, [key]: { ...day, close: e.target.value } }))}
                          inputProps={{ step: 300 }}
                          sx={{ width: 110 }}
                          label="Закрытие"
                        />
                      </Box>
                    );
                  })}
                </Box>

                {/* Закрытые дни */}
                <Box sx={{ flex: "1 1 220px", minWidth: 220 }}>
                  <Typography variant="subtitle2" gutterBottom>Закрытые дни (праздники)</Typography>
                  <Box sx={{ maxHeight: 220, overflowY: "auto", mb: 1 }}>
                    {pointClosures.map((c, i) => (
                      <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                        <Chip label={`${c.date}${c.reason ? " — " + c.reason : ""}`} size="small" />
                        <IconButton size="small" onClick={() => setPointClosures(arr => arr.filter((_, j) => j !== i))}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      type="date"
                      size="small"
                      label="Дата"
                      value={newClosureDate}
                      onChange={(e) => setNewClosureDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      label="Причина (необяз.)"
                      value={newClosureReason}
                      onChange={(e) => setNewClosureReason(e.target.value)}
                      fullWidth
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        if (!newClosureDate) return;
                        const exists = pointClosures.find(c => c.date === newClosureDate);
                        if (exists) return;
                        setPointClosures(arr => [...arr, { date: newClosureDate, reason: newClosureReason || null }]);
                        setNewClosureDate("");
                        setNewClosureReason("");
                      }}
                    >
                      Добавить
                    </Button>
                  </Box>
                </Box>

              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPointDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleSavePoint} variant="contained">
            {editingPoint ? "Сохранить" : "Добавить"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ ДИАЛОГ СТАТУСА ============ */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingStatus ? "Редактировать статус" : "Добавить статус"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Название статуса"
            fullWidth
            variant="outlined"
            value={newStatus.name}
            onChange={(e) =>
              setNewStatus({ ...newStatus, name: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleSaveStatus} variant="contained">
            {editingStatus ? "Сохранить" : "Добавить"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ ДИАЛОГ ИСТОЧНИКА ПРИВЛЕЧЕНИЯ ============ */}
      <Dialog
        open={sourceDialogOpen}
        onClose={() => setSourceDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingSource
            ? "Редактировать источник привлечения"
            : "Добавить источник привлечения"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Название источника"
                fullWidth
                value={newSource.name}
                onChange={(e) =>
                  setNewSource({ ...newSource, name: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Описание"
                fullWidth
                multiline
                rows={3}
                value={newSource.description}
                onChange={(e) =>
                  setNewSource({ ...newSource, description: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSourceDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleSaveSource} variant="contained">
            {editingSource ? "Сохранить" : "Добавить"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ ДИАЛОГ КАТЕГОРИИ РАСХОДОВ ============ */}
      <Dialog
        open={expenseCategoryDialogOpen}
        onClose={() => setExpenseCategoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingExpenseCategory
            ? "Редактировать категорию расходов"
            : "Добавить категорию расходов"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Название категории"
            fullWidth
            variant="outlined"
            value={newExpenseCategory.name}
            onChange={(e) =>
              setNewExpenseCategory({ ...newExpenseCategory, name: e.target.value })
            }
            placeholder="Например: Транспорт"
            helperText="Введите название на русском языке. Код будет сгенерирован автоматически."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpenseCategoryDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleSaveExpenseCategory} variant="contained">
            {editingExpenseCategory ? "Сохранить" : "Добавить"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ ДИАЛОГ ДОГОВОРА ============ */}
      <Dialog
        open={contractDialogOpen}
        onClose={() => setContractDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editingContract ? "Редактировать договор" : "Добавить договор"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={12}>
              <TextField
                autoFocus
                label="Название договора"
                fullWidth
                variant="outlined"
                value={newContract.name}
                onChange={(e) =>
                  setNewContract({ ...newContract, name: e.target.value })
                }
                placeholder="Например: Договор аренды коньков"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Описание"
                fullWidth
                variant="outlined"
                value={newContract.description}
                onChange={(e) =>
                  setNewContract({ ...newContract, description: e.target.value })
                }
                placeholder="Краткое описание договора"
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                После создания шаблона вы сможете добавить содержимое с помощью WYSIWYG редактора
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContractDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleSaveContract} variant="contained">
            {editingContract ? "Сохранить" : "Добавить"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ ДИАЛОГ РЕДАКТОРА ДОКУМЕНТОВ ============ */}
      <Dialog
        open={documentEditorOpen}
        onClose={() => setDocumentEditorOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Редактор документа</Typography>
            <IconButton onClick={() => setDocumentEditorOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {documentEditorOpen && editingDocumentId && (
            <DocumentEditor
              templateId={editingDocumentId}
              onSave={() => {
                setDocumentEditorOpen(false);
                fetchContracts();
              }}
              onClose={() => setDocumentEditorOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default RentalBusinessSettings;
